import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { RecessedSurface } from '../../core/recessed-surface/RecessedSurface';
import { ShortcutTooltip, type ShortcutTooltipProps } from '../../core/shortcut-tooltip/ShortcutTooltip';

const UNSELECTED_VALUE = '__split-button-unselected__';

// Options sit inside the pill's 2px padding: the small control height minus that inset.
const OPTION_HEIGHT = 'h-[calc(var(--spacing-control-sm)-var(--spacing-xxs)*2)]';
const OPTION_WIDTH = 'w-[calc(var(--spacing-control-sm)-var(--spacing-xxs)*2)]';
const OPTION_MIN_WIDTH = 'min-w-[calc(var(--spacing-control-sm)-var(--spacing-xxs)*2)]';
export type SplitButtonOption = {
    readonly icon?: ReactNode;
    readonly label?: ReactNode;
    readonly shortcutTooltip?: Omit<ShortcutTooltipProps, 'children'>;
    readonly title?: string;
    readonly value: string;
};

export type SplitButtonProps = {
    /** Names the group for assistive tech when `label` is left empty. */
    readonly ariaLabel?: string;
    readonly defaultValue?: string;
    readonly disabled?: boolean;
    readonly label: string;
    readonly onOptionChange: (value: string) => void;
    readonly options: readonly SplitButtonOption[];
    readonly showOptionSeparatorWhenUnselected?: boolean;
    readonly value?: string | null;
};

export const SplitButton: FunctionComponent<SplitButtonProps> = ({
    ariaLabel,
    defaultValue,
    disabled = false,
    label,
    onOptionChange,
    options,
    showOptionSeparatorWhenUnselected = false,
    value,
}) => {
    const [internalValue, setInternalValue] = useState(
        value === null ? undefined : (value ?? defaultValue ?? options[0]?.value),
    );
    const selectedValue = value === null ? undefined : (value ?? internalValue);
    const radioGroupValue = value === null ? UNSELECTED_VALUE : selectedValue;
    const hasLabelOptions = options.some((option) => option.label !== undefined);
    const isOptionSeparatorVisible = showOptionSeparatorWhenUnselected && selectedValue === undefined;

    useEffect(() => {
        if (value === null) {
            setInternalValue(undefined);
        } else if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value]);

    const handleValueChange = (nextValue: string): void => {
        setInternalValue(nextValue);
        onOptionChange(nextValue);
    };

    return (
        <span className={clsx('flex items-center gap-m font-sans', disabled && 'pointer-events-none opacity-50')}>
            {label}
            <RadioGroup
                aria-label={ariaLabel}
                className={clsx(
                    'inline-flex items-center justify-center',
                    hasLabelOptions ? 'gap-s' : 'gap-xs',
                    'border-none rounded-full outline-none',
                    'font-sans font-medium no-underline',
                    'transition-all duration-200 ease-in-out',
                    '[&>svg]:h-4 [&>svg]:w-4',
                    'focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-focus-ring focus-visible:outline-offset-2',
                    'text-sm leading-[1.25rem] p-xxs',
                    'bg-surface-hover text-text-secondary shadow-action-secondary',
                )}
                defaultValue={value === null ? undefined : (defaultValue ?? options[0]?.value)}
                disabled={disabled}
                value={radioGroupValue}
                onValueChange={handleValueChange}
            >
                {options.map((option, index) => {
                    const isSelected = selectedValue === option.value;
                    const hasLabel = option.label !== undefined;

                    const optionControl = (
                        <RadioGroupItem
                            aria-label={option.title}
                            asChild={true}
                            key={option.value}
                            title={option.title}
                            value={option.value}
                        >
                            <div
                                className={clsx(
                                    'flex items-center justify-center cursor-pointer',
                                    OPTION_HEIGHT,
                                    hasLabel ? OPTION_MIN_WIDTH : OPTION_WIDTH,
                                    'bg-transparent border-0 rounded-full',
                                    'transition-[background-color,color,box-shadow] duration-200 ease-in-out',
                                    '[&_svg]:self-center [&_svg]:transition-colors',
                                    isSelected
                                        ? 'text-accent'
                                        : '[&_svg]:text-text-content-faint hover:bg-surface-raised hover:shadow-action-secondary-hover hover:[&_svg]:text-text-content-subtle',
                                    'focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-focus-ring focus-visible:outline-offset-1',
                                )}
                            >
                                <RecessedSurface
                                    active={isSelected}
                                    as="span"
                                    className="!rounded-full !p-xxs"
                                    hoverActive={true}
                                    hoverSurfaceShadow="none"
                                    hoverSurfaceTone="inherit"
                                    inactiveBehavior="preserve-structure"
                                    placement="inside"
                                    pocketTone="orange"
                                    surfaceClassName={clsx(
                                        'h-full items-center justify-center',
                                        '!rounded-full',
                                        isSelected && '!text-accent',
                                    )}
                                    surfaceShadow="sm"
                                    surfaceTone="gray"
                                    transition="none"
                                >
                                    {hasLabel ? (
                                        <span className="px-s text-xxs leading-[1rem] whitespace-nowrap">
                                            {option.label}
                                        </span>
                                    ) : (
                                        option.icon
                                    )}
                                </RecessedSurface>
                            </div>
                        </RadioGroupItem>
                    );
                    const optionElement = (
                        <span key={option.value}>
                            {option.shortcutTooltip === undefined ? (
                                optionControl
                            ) : (
                                <ShortcutTooltip {...option.shortcutTooltip}>{optionControl}</ShortcutTooltip>
                            )}
                        </span>
                    );

                    if (!showOptionSeparatorWhenUnselected || index === options.length - 1) {
                        return [optionElement];
                    }

                    return [
                        optionElement,
                        <span
                            aria-hidden={true}
                            className={clsx(
                                'h-[1.25rem] w-px shrink-0',
                                isOptionSeparatorVisible ? 'bg-border-subtle' : 'bg-transparent',
                            )}
                            key={`${option.value}-separator`}
                        />,
                    ];
                })}
            </RadioGroup>
        </span>
    );
};
